import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useMediaQuery, useTheme } from '@mui/material';

import { serviceOrdersFilterLink } from '@/features/service-tasks';
import type { FilterValues } from '@/shared/ui/GeneratikTable';

export interface ViewMoreAction {
  label: string;
  onClick?: () => void;
  expanded?: boolean;
  collapsedCount?: number;
  onCollapsed?: () => void;
}

export const useIsMobileDashboard = (): boolean => {
  const theme = useTheme();
  return useMediaQuery(theme.breakpoints.down('md'));
};

export const useViewMoreList = <T>(
  items: T[],
  limit: number,
  desktopAction?: () => void,
): { visible: T[]; viewMore?: ViewMoreAction } => {
  const { t } = useTranslation();
  const isMobile = useIsMobileDashboard();
  const [expanded, setExpanded] = useState(false);
  const [showAll, setShowAll] = useState(false);

  if (!isMobile) {
    return {
      visible: items.slice(0, limit),
      viewMore: { label: t('services_dashboard.view_more'), onClick: desktopAction },
    };
  }

  if (items.length <= limit) return { visible: items };

  return {
    visible: expanded || showAll ? items : items.slice(0, limit),
    viewMore: {
      label: t(expanded ? 'services_dashboard.show_less' : 'services_dashboard.view_more'),
      onClick: () => {
        if (!expanded) setShowAll(true);
        setExpanded(!expanded);
      },
      expanded,
      collapsedCount: limit,
      onCollapsed: () => setShowAll(false),
    },
  };
};

export const useFilteredViewMoreList = <T>(
  items: T[],
  limit: number,
  filters: FilterValues,
): { visible: T[]; viewMore?: ViewMoreAction } => {
  const navigate = useNavigate();
  return useViewMoreList(items, limit, () => navigate(serviceOrdersFilterLink(filters)));
};
